import { useState } from 'react'
import { Bookmark, Loader2, Plus, Trash2 } from 'lucide-react'
import type { Report } from 'powerbi-client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import {
  useCreateReportFilter,
  useDeleteReportFilter,
  useReportFilters,
} from '@/hooks/useReportFilters'
import type { SavedReportFilterRead } from '@/types/reportFilters'
import { toast } from '@/stores/useToastStore'
import { getErrorMessage } from '@/utils/getErrorMessage'

const MAX_NAME_LENGTH = 255

interface SavedReportFiltersProps {
  planId: string
  /** Report type of the Power BI page currently shown; null until it is known. */
  reportType: string | null
  getReport: () => Report | null
  disabled?: boolean
}

export function SavedReportFilters({
  planId,
  reportType,
  getReport,
  disabled = false,
}: SavedReportFiltersProps) {
  const [saveOpen, setSaveOpen] = useState(false)
  const [name, setName] = useState('')
  const [isCapturing, setIsCapturing] = useState(false)
  const [pendingDelete, setPendingDelete] =
    useState<SavedReportFilterRead | null>(null)

  const { data: filters = [], isLoading } = useReportFilters(planId, reportType)
  const createFilter = useCreateReportFilter(planId)
  const deleteFilter = useDeleteReportFilter(planId, reportType ?? '')

  const trimmedName = name.trim()
  const isSaving = isCapturing || createFilter.isPending

  const handleApply = async (filter: SavedReportFilterRead) => {
    const report = getReport()
    if (!report) return
    try {
      await report.bookmarksManager.applyState(filter.bookmark_state)
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to apply saved filter'))
    }
  }

  const handleSave = async () => {
    const report = getReport()
    if (!report || !reportType || !trimmedName) return
    setIsCapturing(true)
    try {
      const bookmark = await report.bookmarksManager.capture({
        allPages: true,
        personalizeVisuals: true,
      })
      if (!bookmark.state) {
        toast.error('Could not capture the current report filters')
        return
      }
      await createFilter.mutateAsync({
        name: trimmedName,
        report_type: reportType,
        bookmark_state: bookmark.state,
      })
      setSaveOpen(false)
      setName('')
    } catch (error) {
      // Backend errors are already toasted by the mutation's onError.
      if (!createFilter.isError) {
        toast.error(getErrorMessage(error, 'Failed to save filter'))
      }
    } finally {
      setIsCapturing(false)
    }
  }

  const handleConfirmDelete = () => {
    if (!pendingDelete) return
    deleteFilter.mutate(pendingDelete.id, {
      onSettled: () => setPendingDelete(null),
    })
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 cursor-pointer gap-1.5"
            disabled={disabled || !reportType}
            aria-label="Saved filters"
          >
            <Bookmark className="h-4 w-4" />
            Saved filters
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel>Saved filters</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {isLoading ? (
            <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : filters.length === 0 ? (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">
              No saved filters yet
            </div>
          ) : (
            filters.map(filter => (
              <DropdownMenuItem
                key={filter.id}
                className="cursor-pointer justify-between gap-2"
                onSelect={() => void handleApply(filter)}
              >
                <span className="truncate">{filter.name}</span>
                <button
                  type="button"
                  className="cursor-pointer rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  aria-label={`Delete saved filter ${filter.name}`}
                  onClick={event => {
                    event.stopPropagation()
                    event.preventDefault()
                    setPendingDelete(filter)
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuItem>
            ))
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="cursor-pointer gap-2"
            onSelect={() => setSaveOpen(true)}
          >
            <Plus className="h-4 w-4" />
            Save current filters
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={saveOpen}
        onOpenChange={open => {
          if (isSaving) return
          setSaveOpen(open)
          if (!open) setName('')
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Save current filters</DialogTitle>
            <DialogDescription>
              Name this combination of filters to restore it later on this
              report.
            </DialogDescription>
          </DialogHeader>
          <Input
            autoFocus
            value={name}
            maxLength={MAX_NAME_LENGTH}
            placeholder="Filter name"
            aria-label="Filter name"
            onChange={event => setName(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter' && trimmedName && !isSaving) {
                void handleSave()
              }
            }}
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSaveOpen(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void handleSave()}
              disabled={!trimmedName || isSaving}
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={pendingDelete !== null}
        onOpenChange={open => {
          if (!open) setPendingDelete(null)
        }}
        title="Delete saved filter"
        description={`Delete "${pendingDelete?.name ?? ''}"? This cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        isLoading={deleteFilter.isPending}
        onConfirm={handleConfirmDelete}
      />
    </>
  )
}
